import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Typography,
  Box,
  Chip,
  Button,
  Skeleton,
  Stack,
  useTheme,
} from '@mui/material';
import {
  Lightbulb as LightbulbIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import api from '../../api/client';

/**
 * SmartGuidance — A single, concise recommendation card
 * derived from existing readiness-score components.
 *
 * Data source: api.progress.getReadinessScore()
 *
 * Logic:
 *  1. Fetch readiness score (already has component breakdown)
 *  2. Find the weakest component → derive a recommendation
 *  3. Fallback for new users: "Start your first interview"
 *
 * variant:
 *  - card: full recommendation (legacy wide layout)
 *  - chip: one-line readiness chip for mobile feed-first layout
 *  - rail: stacked panel for the dashboard right rail (~300px)
 */

const COMPONENT_ADVICE = {
  score_quality: {
    label: 'Answer Quality',
    advice: 'Focus on structuring your answers using the STAR method to boost your scores.',
    action: 'Practice behavioral questions',
    color: 'warning',
  },
  practice_volume: {
    label: 'Practice Volume',
    advice: 'Consistency is key! Try to complete at least 2-3 interviews per week.',
    action: 'Start an interview now',
    color: 'info',
  },
  consistency: {
    label: 'Consistency',
    advice: 'Build a regular practice habit. Even short daily sessions make a big difference.',
    action: 'Set up a schedule',
    color: 'secondary',
  },
};

const NEW_USER_GUIDANCE = {
  label: 'Get Started',
  advice: 'Welcome! Start your first mock interview to begin your preparation journey.',
  action: 'Start your first interview',
  color: 'primary',
};

export default function SmartGuidance({ onViewProgress, variant = 'card' }) {
  const theme = useTheme();
  const [guidance, setGuidance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchGuidance() {
      try {
        const res = await api.progress.getReadinessScore();
        if (cancelled) return;

        const data = res.data || res;
        const components = data.components || {};
        const totalScore = data.score ?? data.readiness_score ?? 0;

        // No data → new user
        if (totalScore === 0 && Object.keys(components).length === 0) {
          setGuidance(NEW_USER_GUIDANCE);
          setLoading(false);
          return;
        }

        // Find weakest component
        let weakestKey = null;
        let weakestVal = Infinity;

        for (const [key, val] of Object.entries(components)) {
          const numVal = typeof val === 'number' ? val : parseFloat(val) || 0;
          if (numVal < weakestVal && COMPONENT_ADVICE[key]) {
            weakestVal = numVal;
            weakestKey = key;
          }
        }

        if (weakestKey && COMPONENT_ADVICE[weakestKey]) {
          setGuidance({
            ...COMPONENT_ADVICE[weakestKey],
            score: weakestVal,
            totalScore,
          });
        } else {
          // All components are strong — celebrate!
          setGuidance({
            label: 'Great Progress!',
            advice: "You're performing well across all areas. Keep pushing for excellence!",
            action: 'Continue practicing',
            color: 'success',
            totalScore,
          });
        }
      } catch (err) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchGuidance();
    return () => { cancelled = true; };
  }, []);

  // Fail silently — don't block the dashboard
  if (error) return null;

  if (loading) {
    if (variant === 'chip') {
      return <Skeleton variant="rounded" width={110} height={28} data-testid="smart-guidance-loading" />;
    }
    return (
      <Paper
        elevation={0}
        sx={{ p: 2.5, mb: 3, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}
      >
        <Skeleton variant="text" width={200} height={28} />
        <Skeleton variant="text" width="80%" height={20} sx={{ mt: 1 }} />
      </Paper>
    );
  }

  if (!guidance) return null;

  const themeColor = theme.palette[guidance.color]?.main || theme.palette.primary.main;

  if (variant === 'chip') {
    const chipLabel =
      guidance.totalScore > 0 ? `Readiness ${guidance.totalScore}` : guidance.label;
    return (
      <Chip
        data-testid="smart-guidance"
        data-variant="chip"
        label={chipLabel}
        size="small"
        color={guidance.color}
        variant="outlined"
        icon={<LightbulbIcon sx={{ fontSize: 16 }} />}
        onClick={onViewProgress || undefined}
        clickable={Boolean(onViewProgress)}
        sx={{
          fontWeight: 600,
          height: 28,
          flexShrink: 0,
          maxWidth: '100%',
        }}
      />
    );
  }

  if (variant === 'rail') {
    return (
      <Paper
        elevation={0}
        data-testid="smart-guidance"
        data-variant="rail"
        sx={{
          p: 1.5,
          mb: 0,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(180deg, ${themeColor}08 0%, transparent 70%)`,
          overflow: 'hidden',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${themeColor}15`,
              color: themeColor,
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            <LightbulbIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              {guidance.label}
            </Typography>
            {guidance.totalScore > 0 && (
              <Chip
                label={`Readiness: ${guidance.totalScore}`}
                size="small"
                color={guidance.color}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', mt: 0.5, maxWidth: '100%' }}
              />
            )}
          </Box>
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="guidance-advice"
          sx={{ display: 'block', mb: 1.25, lineHeight: 1.45 }}
        >
          {guidance.advice}
        </Typography>
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          {onViewProgress && (
            <Button
              size="small"
              fullWidth
              onClick={onViewProgress}
              data-testid="smart-guidance-view-progress"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              View progress
            </Button>
          )}
          <Chip
            label={guidance.action}
            icon={guidance.color === 'success' ? <TrophyIcon fontSize="small" /> : <TrendingUpIcon fontSize="small" />}
            color={guidance.color}
            size="small"
            variant="outlined"
            component="span"
            sx={{
              fontWeight: 600,
              width: '100%',
              maxWidth: '100%',
              justifyContent: 'flex-start',
              height: 'auto',
              py: 0.75,
              '& .MuiChip-label': { whiteSpace: 'normal' },
              cursor: 'default',
              pointerEvents: 'none',
            }}
            data-testid="guidance-action-badge"
          />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      data-testid="smart-guidance"
      data-variant="card"
      sx={{
        p: { xs: 2, md: 2.5 },
        mb: 3,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        background: `linear-gradient(90deg, ${themeColor}06 0%, transparent 100%)`,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${themeColor}15`,
          color: themeColor,
          flexShrink: 0,
        }}
      >
        <LightbulbIcon />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {guidance.label}
          </Typography>
          {guidance.totalScore > 0 && (
            <Chip
              label={`Readiness: ${guidance.totalScore}`}
              size="small"
              color={guidance.color}
              variant="outlined"
              sx={{ height: 22, fontSize: '0.75rem' }}
            />
          )}
          {onViewProgress && (
            <Button
              size="small"
              onClick={onViewProgress}
              data-testid="smart-guidance-view-progress"
              sx={{
                textTransform: 'none',
                minWidth: 0,
                px: 0.5,
                py: 0,
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'text.secondary',
                '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
              }}
            >
              View progress →
            </Button>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" data-testid="guidance-advice">
          {guidance.advice}
        </Typography>
      </Box>

      <Chip
        label={guidance.action}
        icon={guidance.color === 'success' ? <TrophyIcon fontSize="small" /> : <TrendingUpIcon fontSize="small" />}
        color={guidance.color}
        size="small"
        variant="outlined"
        component="span"
        sx={{
          fontWeight: 600,
          flexShrink: 0,
          display: { xs: 'none', sm: 'flex' },
          cursor: 'default',
          pointerEvents: 'none',
        }}
        data-testid="guidance-action-badge"
      />
    </Paper>
  );
}

SmartGuidance.propTypes = {
  onViewProgress: PropTypes.func,
  variant: PropTypes.oneOf(['card', 'chip', 'rail']),
};
